import React, { useCallback, useEffect, useState } from "react";
import { Box, Flex, Text, useBreakpointValue } from "@chakra-ui/react";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronDown,
  FaChevronRight,
  FaRegCircle,
  FaRegClock,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import { getToken } from "../token";
import { FORUM, TRANSITION } from "../Theme/styles";
import { findCategory, TASK_PATH } from "../categories";
import { priorityName } from "../priorities";
import { formatShort } from "../dates";
import StageEditor from "../Components/StageEditor";
import SecretVideo from "../Components/SecretVideo";
import { isRcon } from "../permissions";

const STAGE_STATUS = {
  planned: { name: "Planowane", color: "#8A93A5", bg: "rgba(138,147,165,0.12)" },
  in_progress: { name: "W trakcie", color: "#6C99FF", bg: "rgba(108,153,255,0.14)" },
  done: { name: "Zakończone", color: "#4FB477", bg: "rgba(79,180,119,0.14)" },
};

function dateText(entry) {
  if (entry.date_label) return entry.date_label;
  if (entry.kind === "roadmap" && entry.start_date && entry.end_date) {
    return `${formatShort(entry.start_date)}–${formatShort(entry.end_date)}`;
  }
  return formatShort(entry.sort_date);
}

const Stage = ({ stage, index, onOpenTask }) => {
  const status = STAGE_STATUS[stage.status] || STAGE_STATUS.planned;

  return (
    <Box
      bg={FORUM.panel}
      border={"1px solid"}
      borderColor={FORUM.obramowanie}
      borderRadius={"lg"}
      p={4}
      flex={"1 1 240px"}
      minW={0}
    >
      <Flex align={"center"} gap={2} mb={3}>
        <Flex
          align={"center"}
          justify={"center"}
          w={"22px"}
          h={"22px"}
          borderRadius={"md"}
          bg={status.bg}
          color={status.color}
          fontSize={"11px"}
          fontWeight={"700"}
          flexShrink={0}
        >
          {index + 1}
        </Flex>
        <Text fontSize={"sm"} fontWeight={"600"} color={FORUM.tekstJasny} noOfLines={1}>
          {stage.name}
        </Text>
        <Box flex={1} />
        <Text
          fontSize={"9px"}
          fontWeight={"700"}
          letterSpacing={"0.08em"}
          textTransform={"uppercase"}
          color={status.color}
          bg={status.bg}
          borderRadius={"full"}
          px={2}
          py={"2px"}
          whiteSpace={"nowrap"}
        >
          {status.name}
        </Text>
      </Flex>

      <Flex direction={"column"} gap={1.5}>
        {stage.items.length === 0 && (
          <Text fontSize={"xs"} color={FORUM.tekstPrzygaszony}>
            Nic tu jeszcze nie stoi.
          </Text>
        )}
        {stage.items.map((item) => (
          <Flex
            key={item.id}
            align={"center"}
            gap={2}
            cursor={item.task_id ? "pointer" : "default"}
            transition={TRANSITION}
            _hover={item.task_id ? { color: FORUM.tekstJasny } : undefined}
            onClick={() => item.task_id && onOpenTask(item.task_id)}
            title={item.task_id ? "Otwórz zadanie" : undefined}
          >
            {item.done ? (
              <FaCheckCircle size={13} color={"#4FB477"} />
            ) : (
              <FaRegCircle size={13} color={FORUM.tekstPrzygaszony} />
            )}
            <Text fontSize={"sm"} color={FORUM.tekst} noOfLines={1}>
              {item.label}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Box>
  );
};

const DeadlineRow = ({ entry, onOpenTask }) => {
  const teams = entry.teams
    .map((k) => findCategory(k)?.name || k)
    .filter(Boolean);
  const meta = [...teams];
  if (entry.piority) meta.push(`priorytet ${priorityName(entry.piority).toLowerCase()}`);
  if (entry.note) meta.push(entry.note);

  const percent = entry.items_total
    ? Math.round((entry.items_done / entry.items_total) * 100)
    : 0;

  return (
    <Flex
      align={"center"}
      gap={{ base: 2, md: 4 }}
      px={{ base: 3, md: 4 }}
      py={2.5}
      borderBottom={"1px solid"}
      borderColor={FORUM.obramowanie}
      cursor={entry.kind === "task" ? "pointer" : "default"}
      transition={TRANSITION}
      _hover={entry.kind === "task" ? { bg: FORUM.panelJasny } : undefined}
      onClick={() => entry.kind === "task" && onOpenTask(entry.id)}
    >
      <Text
        fontSize={{ base: "xs", md: "sm" }}
        fontWeight={"600"}
        color={FORUM.tekst}
        minW={{ base: "52px", md: "86px" }}
        flexShrink={0}
      >
        {dateText(entry)}
      </Text>

      <Box flex={1} minW={0}>
        <Text fontSize={"sm"} color={FORUM.tekstJasny} noOfLines={1}>
          {entry.title}
        </Text>
        {meta.length > 0 && (
          <Text fontSize={"11px"} color={FORUM.tekstPrzygaszony} noOfLines={1}>
            {meta.join(" · ")}
          </Text>
        )}
      </Box>

      <Flex align={"center"} gap={2} flexShrink={0}>
        <Box
          display={{ base: "none", sm: "block" }}
          w={"90px"}
          h={"4px"}
          borderRadius={"full"}
          bg={FORUM.plakietka}
          overflow={"hidden"}
        >
          <Box
            h={"100%"}
            w={`${percent}%`}
            bg={percent === 100 ? "#4FB477" : "#6C99FF"}
            transition={TRANSITION}
          />
        </Box>
        <Text fontSize={"11px"} color={FORUM.tekstPrzygaszony} minW={"32px"} textAlign={"right"}>
          {entry.items_done}/{entry.items_total}
        </Text>
      </Flex>
    </Flex>
  );
};

const Roadmap = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [tab, setTab] = useState("etapy");
  const waskoEkran = useBreakpointValue({ base: true, md: false });
  const pokazEtapy = !waskoEkran || tab === "etapy";
  const pokazTerminy = !waskoEkran || tab === "terminy";
  const navigate = useNavigate();

  const load = useCallback(() => {
    axios
      .get(`${API_URL}/roadmap`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((res) => setData(res.data))
      .catch((err) => console.error("Nie udało się wczytać roadmapy", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openTask = (id) => navigate(`${TASK_PATH}/${id}`);

  if (loading) {
    return (
      <Text fontSize={"sm"} color={FORUM.tekstPrzygaszony}>
        Wczytuję roadmapę…
      </Text>
    );
  }

  const stages = data?.stages || [];
  const doneStages = stages.filter((s) => s.status === "done");
  const deadlines = data?.deadlines || [];
  const progress = data?.progress || 0;

  return (
    <>
    <SecretVideo word={"kurak"} src={"/kurak.mp4"} volume={0.5} />
    <Flex
      direction={"column"}
      gap={4}
      flex={{ base: "0 0 auto", md: "1" }}
      minH={0}
      overflowY={{ base: "visible", md: "auto" }}
      pr={{ base: 0, md: 1 }}
    >
      <Flex gap={4} wrap={"wrap"} align={"stretch"} flexShrink={0}>
        <Flex direction={"column"} gap={0.5} justify={"center"} flex={"1 1 240px"}>
          <Text
            fontSize={"11px"}
            fontWeight={"500"}
            letterSpacing={"0.08em"}
            textTransform={"uppercase"}
            color={FORUM.tekstPrzygaszony}
          >
            Zadania główne
          </Text>
          <Text fontSize={"xl"} fontWeight={"600"} lineHeight={"1.1"} color={FORUM.tekstJasny}>
            Roadmapa Kacperka
          </Text>
        </Flex>

        <Box
          bg={FORUM.panel}
          border={"1px solid"}
          borderColor={FORUM.obramowanie}
          borderRadius={"lg"}
          px={5}
          py={3}
          flex={"1 1 280px"}
        >
          <Text fontSize={"xs"} color={FORUM.tekstPrzygaszony} mb={1}>
            Postęp projektu
          </Text>
          <Flex align={"center"} gap={3}>
            <Text fontSize={"2xl"} fontWeight={"700"} color={"#6C99FF"} lineHeight={"1"}>
              {progress}%
            </Text>
            <Box flex={1} h={"6px"} borderRadius={"full"} bg={FORUM.plakietka} overflow={"hidden"}>
              <Box h={"100%"} w={`${progress}%`} bg={"#6C99FF"} transition={TRANSITION} />
            </Box>
          </Flex>
        </Box>

        <Flex
          direction={"column"}
          gap={1}
          justify={"center"}
          bg={FORUM.panel}
          border={"1px solid"}
          borderColor={FORUM.obramowanie}
          borderRadius={"lg"}
          px={5}
          py={3}
          flex={"0 1 300px"}
        >
          <Flex align={"center"} gap={2}>
            <FaCalendarAlt size={12} color={FORUM.tekstPrzygaszony} />
            <Text fontSize={"xs"} color={FORUM.tekstPrzygaszony}>
              Planowana premiera:
            </Text>
            <Text fontSize={"xs"} fontWeight={"600"} color={"#6C99FF"}>
              {data?.settings?.release_label || "—"}
            </Text>
          </Flex>
          <Flex align={"center"} gap={2}>
            <FaRegClock size={12} color={FORUM.tekstPrzygaszony} />
            <Text fontSize={"xs"} color={FORUM.tekstPrzygaszony}>
              Ostatnia aktualizacja:
            </Text>
            <Text fontSize={"xs"} fontWeight={"600"} color={"#6C99FF"}>
              {data?.settings?.updated_at
                ? new Date(data.settings.updated_at).toLocaleDateString("pl-PL", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                : "—"}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      <Flex display={{ base: "flex", md: "none" }} gap={2} flexShrink={0}>
        {[
          { key: "etapy", name: "Etapy" },
          { key: "terminy", name: "Terminy" },
        ].map((o) => (
          <Box
            as={"button"}
            key={o.key}
            flex={1}
            fontSize={"sm"}
            py={2}
            borderRadius={"md"}
            border={"1px solid"}
            borderColor={tab === o.key ? "#6C99FF" : FORUM.obramowanie}
            bg={tab === o.key ? "rgba(108,153,255,0.12)" : FORUM.panel}
            color={tab === o.key ? FORUM.tekstJasny : FORUM.tekstPrzygaszony}
            fontWeight={tab === o.key ? "600" : "400"}
            transition={TRANSITION}
            onClick={() => setTab(o.key)}
          >
            {o.name}
          </Box>
        ))}
      </Flex>

      {pokazEtapy && isRcon() && (
        <Flex gap={2} flexShrink={0}>
          <Box
            as={"button"}
            fontSize={"xs"}
            px={3}
            py={1.5}
            borderRadius={"md"}
            border={"1px solid"}
            borderColor={FORUM.obramowanie}
            color={FORUM.tekst}
            transition={TRANSITION}
            _hover={{ borderColor: "#6C99FF", color: FORUM.tekstJasny }}
            onClick={() => setEditing(true)}
          >
            Ułóż roadmapę
          </Box>
        </Flex>
      )}

      {pokazEtapy && doneStages.length > 0 && (
        <Flex
          as={"button"}
          align={"center"}
          gap={2}
          alignSelf={"flex-start"}
          px={3}
          py={1.5}
          borderRadius={"md"}
          border={"1px solid"}
          borderColor={FORUM.obramowanie}
          bg={FORUM.panel}
          color={FORUM.tekstPrzygaszony}
          transition={TRANSITION}
          _hover={{ borderColor: "#4FB477", color: FORUM.tekstJasny }}
          onClick={() => setShowDone(!showDone)}
          flexShrink={0}
        >
          {showDone ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
          <Text fontSize={"xs"}>
            {doneStages.length}{" "}
            {doneStages.length === 1
              ? "etap zakończony"
              : doneStages.length % 10 >= 2 &&
                doneStages.length % 10 <= 4 &&
                (doneStages.length % 100 < 12 || doneStages.length % 100 > 14)
              ? "etapy zakończone"
              : "etapów zakończonych"}
          </Text>
          <Text fontSize={"xs"} color={"#4FB477"}>
            {showDone ? "ukryj" : "pokaż"}
          </Text>
        </Flex>
      )}

      {pokazEtapy && (
      <Flex gap={4} wrap={"wrap"} flexShrink={0}>
        {stages.length === 0 ? (
          <Box
            bg={FORUM.panel}
            border={"1px solid"}
            borderColor={FORUM.obramowanie}
            borderRadius={"lg"}
            px={4}
            py={8}
            w={"100%"}
            textAlign={"center"}
          >
            <Text fontSize={"sm"} color={FORUM.tekst} mb={1}>
              Roadmapy jeszcze nie ma.
            </Text>
            <Text fontSize={"xs"} color={FORUM.tekstPrzygaszony}>
              {isRcon()
                ? "Dodaj pierwszy etap, a potem wrzuć do niego zadania albo własne wpisy."
                : "Administracja RCON jeszcze jej nie ułożyła."}
            </Text>
          </Box>
        ) : (
          stages
            .filter((s) => s.status !== "done")
            .map((stage) => (
              <Stage
                key={stage.id}
                stage={stage}
                index={stages.indexOf(stage)}
                onOpenTask={openTask}
              />
          ))
        )}
      </Flex>
      )}

      {pokazEtapy && showDone && doneStages.length > 0 && (
        <Box
          maxH={{ base: "none", md: "300px" }}
          overflowY={{ base: "visible", md: "auto" }}
          flexShrink={0}
          pr={{ base: 0, md: 1 }}
        >
          <Flex gap={4} wrap={"wrap"}>
            {doneStages.map((stage) => (
              <Stage
                key={stage.id}
                stage={stage}
                index={stages.indexOf(stage)}
                onOpenTask={openTask}
              />
            ))}
          </Flex>
        </Box>
      )}

      {pokazTerminy && deadlines.length > 0 && (
        <Box
          bg={FORUM.panel}
          border={"1px solid"}
          borderColor={FORUM.obramowanie}
          borderRadius={"lg"}
          overflow={"hidden"}
          minH={0}
        >
          <Flex align={"center"} gap={2} px={4} py={3}>
            <FaCalendarAlt size={12} color={FORUM.tekstPrzygaszony} />
            <Text fontSize={"sm"} fontWeight={"600"} color={FORUM.tekstJasny}>
              Najbliższe deadline'y
            </Text>
          </Flex>
          <Box
            maxH={{ base: "none", md: "420px" }}
            overflowY={{ base: "visible", md: "auto" }}
          >
            {deadlines.map((entry) => (
              <DeadlineRow key={`${entry.kind}-${entry.id}`} entry={entry} onOpenTask={openTask} />
            ))}
          </Box>
        </Box>
      )}

      {editing && (
        <StageEditor
          stages={stages}
          onClose={() => setEditing(false)}
          onChange={load}
          settings={data?.settings}
        />
      )}
    </Flex>
    </>
  );
};

export default Roadmap;
